import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useGetOne, useNotify, useRefresh } from 'react-admin';
import { DialogCloseButton } from '@app/misc/DialogCloseButton';
import type { HighlightedResourceSummary } from '@wwf/sdk';
import { HighlightedResourceDrawerForm } from './HighlightedResourceDrawerForm';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DrawerState } from '@app/misc/DrawerContext';
import { useDrawer } from '@app/misc/DrawerContext';

type HighlightedResourceDrawerMode = 'create' | 'edit' | 'show';

export function useHighlightedResourceDrawer() {
    const drawer = useDrawer();
    const openDrawer = useCallback(
        (mode: HighlightedResourceDrawerMode, selectedId: string | null) =>
            drawer.openDrawer(state => (
                <HighlightedResourceDrawer
                    {...state}
                    mode={mode}
                    selectedId={selectedId}
                />
            )),
        [drawer]
    );
    const closeDrawer = useCallback(() => {
        drawer.closeDrawer();
    }, [drawer]);
    return { openDrawer, closeDrawer };
}

export function HighlightedResourceDrawer({
    open,
    onClose,
    mode,
    selectedId,
}: DrawerState & {
    mode: HighlightedResourceDrawerMode;
    selectedId: string | null;
}) {
    const notify = useNotify();
    const refresh = useRefresh();

    const [viewMode, setViewMode] =
        useState<HighlightedResourceDrawerMode>(mode);

    useEffect(() => {
        if (open) {
            setViewMode(mode);
        }
    }, [open, mode, selectedId]);

    const title = useMemo(() => {
        if (viewMode === 'create') return 'Create highlighted resource';
        if (viewMode === 'show') return 'Highlighted resource';
        return 'Edit highlighted resource';
    }, [viewMode]);

    const loadRecord = viewMode === 'edit' || viewMode === 'show';

    const { data: selectedRecord, isPending: isPendingSelectedRecord } =
        useGetOne<HighlightedResourceSummary>(
            'highlightedResource',
            { id: selectedId as any },
            { enabled: open && loadRecord && !!selectedId }
        );

    const canToggleShowEdit = !!selectedId && viewMode !== 'create';

    return (
        <Box sx={{ position: 'relative', height: '100%' }}>
            <DialogCloseButton onClose={onClose} />
            <Box p={3} pt={4}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                    gap={1}
                >
                    <Typography variant="h6">{title}</Typography>
                    {canToggleShowEdit &&
                        (viewMode === 'show' ? (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => setViewMode('edit')}
                            >
                                Edit
                            </Button>
                        ) : (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => setViewMode('show')}
                            >
                                View
                            </Button>
                        ))}
                </Stack>

                <HighlightedResourceDrawerForm
                    mode={viewMode}
                    record={loadRecord ? selectedRecord : undefined}
                    loading={loadRecord && isPendingSelectedRecord}
                    onClose={onClose}
                    onSuccess={() => {
                        refresh();
                        notify('Saved', { type: 'info' });
                    }}
                />
            </Box>
        </Box>
    );
}
